import { useState } from 'react';
import styled from 'styled-components';
import { updateMusicPlayer } from '../../../../scripts/musicPlayer';
import theme from '../../../../UI/theme';
import { ButtonRowContainer, ModalContainer, ModalHeader, ModalText } from '../../../panel/ModalStyles';
import Button from '../../../UiComponents/Button';
import InputField from '../../../UiComponents/InputField';
import Modal from '../../../UiComponents/Modal';
import NeutralSpinner from '../../../UiComponents/Animations/NeutralSpinner';
import { checkAvailablePlayers } from '../providerUtils';
import { musicPlayerProviders } from '../musicPlayerProviders';

const steps = {
    CREDENTIALS: 'CREDENTIALS',
    REVIEW: 'REVIEW',
    SUCCESS: 'SUCCESS'
}

const PlayerList = styled.div`
    display: grid;
    grid-template-columns: 1fr;
    gap: 6px;
`

const PlayerItem = styled.label`
    background-color: ${theme.colors.darkSpace80};
    padding: 10px;
`

const ErrorMessage = styled.label`
    color: ${theme.colors.raspberryRed};
`

const SoundtrackReauthorizeModal = ({ show, close, musicPlayers, onReauthorized }) => {
    const [step, setStep] = useState(steps.CREDENTIALS);
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [isBusy, setIsBusy] = useState(false);
    const [errorMessage, setErrorMessage] = useState();
    const [authorization, setAuthorization] = useState();
    const [matchedPlayers, setMatchedPlayers] = useState([]);

    const reset = () => {
        setStep(steps.CREDENTIALS);
        setEmail('');
        setPassword('');
        setIsBusy(false);
        setErrorMessage();
        setAuthorization();
        setMatchedPlayers([]);
    }

    const closeModal = () => {
        if (isBusy) return;
        reset();
        close();
    }

    const signIn = async () => {
        if (!email.trim() || !password || isBusy) return;

        setIsBusy(true);
        setErrorMessage();

        try {
            const providerData = await checkAvailablePlayers(
                musicPlayerProviders.SOUNDTRACK_YOUR_BRAND.key,
                { email: email.trim(), password }
            );

            if (!providerData?.token || !providerData?.refreshToken || !Array.isArray(providerData?.zones)) {
                throw new Error('Soundtrack login failed');
            }

            const availableZoneIds = new Set(providerData.zones.map(zone => zone.id));
            const players = musicPlayers.filter(musicPlayer => availableZoneIds.has(musicPlayer.remoteId));

            if (players.length === 0) {
                throw new Error('No matching players');
            }

            setAuthorization({
                token: providerData.token,
                refreshToken: providerData.refreshToken
            });
            setMatchedPlayers(players);
            setStep(steps.REVIEW);
        } catch (error) {
            setErrorMessage(
                error.message === 'No matching players'
                    ? 'Login succeeded, but this Soundtrack user does not have access to the affected players.'
                    : 'Unable to sign in to Soundtrack. Check the email and password and try again.'
            );
        } finally {
            setPassword('');
            setIsBusy(false);
        }
    }

    const reauthorizePlayers = async () => {
        if (!authorization || matchedPlayers.length === 0 || isBusy) return;

        setIsBusy(true);
        setErrorMessage();

        try {
            for (const musicPlayer of matchedPlayers) {
                await updateMusicPlayer({
                    musicPlayerId: musicPlayer.musicPlayerId,
                    token: authorization.token,
                    refreshToken: authorization.refreshToken
                });
            }

            setAuthorization();
            setEmail('');
            setStep(steps.SUCCESS);
            onReauthorized(matchedPlayers.map(musicPlayer => musicPlayer.musicPlayerId));
        } catch (error) {
            setErrorMessage('Unable to update every player. Please try again.');
        } finally {
            setIsBusy(false);
        }
    }

    return <Modal show={show} close={closeModal}>
        <ModalContainer>
            <ModalHeader>Reauthorize Soundtrack</ModalHeader>

            {step === steps.CREDENTIALS ? <>
                <ModalText>
                    Sign in with the Soundtrack account used by these players, the same account used on setup.
                </ModalText>
                <label>Email</label>
                <InputField
                    type='email'
                    autoComplete='username'
                    value={email}
                    disabled={isBusy}
                    onChange={(event) => setEmail(event.target.value)}
                />
                <label>Password</label>
                <InputField
                    type='password'
                    autoComplete='current-password'
                    value={password}
                    disabled={isBusy}
                    onChange={(event) => setPassword(event.target.value)}
                    onKeyDown={(event) => event.key === 'Enter' && signIn()}
                />
                {errorMessage ? <ErrorMessage>{errorMessage}</ErrorMessage> : <></>}
                {isBusy ? <NeutralSpinner /> : <></>}
                <ButtonRowContainer>
                    <Button secondary onClick={closeModal} disabled={isBusy}>Cancel</Button>
                    <Button primary onClick={signIn} disabled={isBusy || !email.trim() || !password}>Continue</Button>
                </ButtonRowContainer>
            </> : <></>}

            {step === steps.REVIEW ? <>
                <ModalText>
                    The following existing players match this Soundtrack account. Their settings and connections will be preserved.
                </ModalText>
                <PlayerList>
                    {matchedPlayers.map(musicPlayer =>
                        <PlayerItem key={musicPlayer.musicPlayerId}>{musicPlayer.name}</PlayerItem>
                    )}
                </PlayerList>
                {errorMessage ? <ErrorMessage>{errorMessage}</ErrorMessage> : <></>}
                {isBusy ? <NeutralSpinner /> : <></>}
                <ButtonRowContainer>
                    <Button secondary onClick={closeModal} disabled={isBusy}>Cancel</Button>
                    <Button primary onClick={reauthorizePlayers} disabled={isBusy}>
                        Reauthorize {matchedPlayers.length} {matchedPlayers.length === 1 ? 'player' : 'players'}
                    </Button>
                </ButtonRowContainer>
            </> : <></>}

            {step === steps.SUCCESS ? <>
                <ModalText>
                    Soundtrack authorization was updated for {matchedPlayers.length} {matchedPlayers.length === 1 ? 'player' : 'players'}.
                </ModalText>
                <ButtonRowContainer>
                    <Button primary onClick={closeModal}>Done</Button>
                </ButtonRowContainer>
            </> : <></>}
        </ModalContainer>
    </Modal>
}

export default SoundtrackReauthorizeModal;
